// esbuild-loader compiles JSX with the classic runtime, so React must be in scope.
import React from "react";
import { useContent } from "@thoughtbot/superglue";
import { ReceiptTextIcon } from "lucide-react";

import { Badge } from "@javascript/components/ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@javascript/components/ui/empty";

type Transaction = {
  id: number;
  createdAt: string;
  createdAtLabel: string;
  invitationName?: string;
  status: "pending" | "paid" | "reversed";
  statusLabel: string;
  chargedAmountLabel?: string;
  providerReference: string;
};

type TransactionsContent = {
  transactions: Transaction[];
};

export default function Transactions() {
  const { transactions } = useContent<TransactionsContent>();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
      <header className="max-w-2xl">
        <p className="text-sm font-medium text-primary">Account</p>
        <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight">Transactions</h1>
        <p className="mt-3 text-muted-foreground">
          A record of every payment started for your invitations.
        </p>
      </header>

      {transactions.length === 0 ? (
        <Empty className="mt-8 border border-solid bg-background">
          <EmptyHeader>
            <EmptyMedia variant="icon" aria-hidden="true">
              <ReceiptTextIcon />
            </EmptyMedia>
            <EmptyTitle>No payments yet</EmptyTitle>
            <EmptyDescription>
              Transactions for publishing an invitation will appear here.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ol className="mt-8 space-y-3" aria-label="Payment history">
          {transactions.map((transaction) => (
            <li key={transaction.id}>
              <article className="min-w-0 rounded-lg border bg-card p-4 shadow-sm sm:p-5">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    {transaction.invitationName && (
                      <h2 className="break-words font-medium text-foreground">{transaction.invitationName}</h2>
                    )}
                    <time dateTime={transaction.createdAt} className="mt-1 block text-sm text-muted-foreground">
                      {transaction.createdAtLabel}
                    </time>
                  </div>
                  <Badge
                    variant={
                      transaction.status === "paid"
                        ? "success"
                        : transaction.status === "reversed"
                          ? "destructive"
                          : "secondary"
                    }
                    className="shrink-0"
                  >
                    {transaction.statusLabel}
                  </Badge>
                </div>

                <dl className="mt-5 grid gap-4 border-t pt-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Charge</dt>
                    <dd className="mt-1 tabular-nums font-semibold">
                      {transaction.chargedAmountLabel ?? "No charge recorded"}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Provider reference
                    </dt>
                    <dd className="mt-1 min-w-0 break-all font-mono text-sm text-foreground">
                      {transaction.providerReference}
                    </dd>
                  </div>
                </dl>
              </article>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
